import { Button, colors, Typography } from '@codility/rondo-ui';
import Image from 'next/image';
import Head from 'next/head';
import styled, { ThemeProvider } from 'styled-components';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { EventId } from '../../services/types';
import { GlobalHeader } from '../Layout/GlobalHeader';
import { landingPageTheme } from '../../theme/landing-page-theme';
import { GlobalStyles } from '../Layout/GlobalStyles';

const PageContainer = styled.div`
  --header-height: 64px;
  width: 100vw;
  height: 100vh;
`;

const Content = styled.main`
  height: calc(100% - var(--header-height));
  background-color: ${colors.ultramarine50};

  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: calc(2 * var(--header-height));
`;

const Title = styled(Typography).attrs({ variant: 'heading1' })`
  && {
    font-size: 3rem;
    line-height: 3.5rem;
    font-weight: 700;
    margin: 2rem 0;
  }
`;

const Message = styled(Typography).attrs({ variant: 'heading2' })`
  && {
    font-size: 1rem;
    line-height: 1.5rem;
    margin: 1.5rem 0;
  }
`;

interface Props {
  id: EventId;
  title: string;
  message: string;
  image: { src: string; alt: string };
  logo?: string;
}

export const ThankYou = ({ id, logo, title, message, image }: Props) => {
  const router = useRouter();
  const { t } = useTranslation('thank-you');
  const goBack = () => {
    router.push(`/events/${id}`).catch(() => alert('Something went wrong'));
  };

  return (
    <ThemeProvider theme={landingPageTheme()}>
      <GlobalStyles />
      <Head>
        <title>CodeEvent</title>
      </Head>
      <PageContainer>
        <GlobalHeader logo={logo} />
        <Content>
          <MessageContainer>
            <Image
              src={image.src}
              height="200px"
              width="200px"
              alt={image.alt}
            />
            <Title>{title}</Title>
            <Message>{message}</Message>
            <Button onClick={goBack} variant="primary">
              {t('goBack')}
            </Button>
          </MessageContainer>
        </Content>
      </PageContainer>
    </ThemeProvider>
  );
};
